import React, { useMemo, useState } from "react";
import Navigation from "../form-navigation/Navigation";
import { t } from "components/CentralDataMangement/translation/Translation";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import { useFormContext } from "../../context/Context";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import FileUpload from "components/common/molecules/FileUpload";
import CheckBoxField from "components/common/atoms/CheckBoxField";
import LabelField from "components/common/atoms/LabelField";
import InfoIcon from "static/images/InfoIcon";
import {
  UPDATE_FIELDS,
  GENERAL_DETAILS,
  UPDATE_FIELD_ERROR,
} from "../../annotation/PlanningConstants";
import { Link } from "react-router-dom";
import ActiveLocation from "static/images/ActiveLocation";
import type {
  AddressDetails,
} from "components/common/molecules/AddressSearchPopUp";
import AddressSearchPopup from "components/common/molecules/AddressSearchPopUp";
import { SetAddressData } from "../form-data/SetAddressData";
import CloneIcon from "static/images/CloneIcon";
import DeleteIcon from "static/images/DeleteIcon";
import { fetchCompanyDetails } from "../form-data/SetCompanyRelatedData";
import AutoCompleteDropdown from "components/common/atoms/AutoCompleteDropdown";
import { getZipFromCityOption, validatePostalCode } from "components/common/utlis/HelperFunctions";
import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";

const GeneralTab: React.FC = () => {
  const { state, dispatch, isViewMode } = useFormContext();

  const fileUploadStyleObject: React.CSSProperties = {
    width: "100%",
  };
  const [validationStatus, setValidationStatus] = useState({
    isValid: false,
    type: "",
  });

  const validStatus = (validation: any) => {
    setValidationStatus({
      isValid: validation.isValid,
      type: validation.type,
    });
  };

  // Validate the zip code: it is mandatory, and for BE/FR/NL the zip/city/country
  // combination is checked against the backend so a mismatch shows an error
  // (previously the address had no validation at all on this tab).
  const validatePlanningZip = async (zip: any, city: any, country: any) => {
    const zipStr = (zip ?? "").toString().trim();
    if (zipStr === "") {
      dispatch({ type: UPDATE_FIELD_ERROR, field: "zipCode", error: t("This field is required") });
      return;
    }
    if (country?.value && [29, 182, 93].includes(country.value) && country?.iso && city?.label) {
      const statusCode = await validatePostalCode(zipStr, country.iso, city.label);
      dispatch({
        type: UPDATE_FIELD_ERROR,
        field: "zipCode",
        error:
          statusCode === 200
            ? ""
            : t("Invalid postal code combination (Enter proper Zip code, City and Country)"),
      });
    } else {
      dispatch({ type: UPDATE_FIELD_ERROR, field: "zipCode", error: "" });
    }
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    if (isViewMode) {return;}

    dispatch({
      type: UPDATE_FIELDS,
      tab: GENERAL_DETAILS,
      field: fieldName,
      value: selectedOption,
    });

    if (fieldName == "country") {
      dispatch({
        type: UPDATE_FIELDS,
        tab: GENERAL_DETAILS,
        field: "city",
        value: null,
      });
    }

    // Selecting a city pre-fills the zip ONLY when it is empty; an existing zip is
    // never overwritten or cleared (per client: postal must not be erased).
    if (fieldName == "city" && selectedOption) {
      const zip = getZipFromCityOption(selectedOption);
      if (zip && !(state.generalDetails.zipCode ?? "").toString().trim()) {
        dispatch({
          type: UPDATE_FIELDS,
          tab: GENERAL_DETAILS,
          field: "zipCode",
          value: zip,
        });
      }
    }

    // Re-check the zip/city/country combination when city or country changes.
    if (fieldName == "city") {
      const currentZip = (state.generalDetails.zipCode ?? "").toString().trim();
      const effectiveZip = currentZip || (selectedOption ? getZipFromCityOption(selectedOption) : "");
      validatePlanningZip(effectiveZip, selectedOption, state.generalDetails.country);
    } else if (fieldName == "country") {
      validatePlanningZip(state.generalDetails.zipCode, null, selectedOption);
    }
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) {return;}
    const { name, value, type } = event.target;
    let updatedValue: string | number | null = value;
    if (type === "checkbox") {
      updatedValue = event.target.checked ? 1 : 0;
    }

    if (name == "zipCode") {
      updatedValue = value.replace(/\s/g, ""); // Remove all spaces
    }

    dispatch({
      type: UPDATE_FIELDS,
      tab: GENERAL_DETAILS,
      field: name,
      value: updatedValue,
    });

    if (name == "zipCode") {
      validatePlanningZip(updatedValue, state.generalDetails.city, state.generalDetails.country);
    }
  };

  const returnFileUploadErrors = (errorResposne: any) => {
    const step = errorResposne["step"];
    const error = errorResposne["error"];
    dispatch({ type: UPDATE_FIELD_ERROR, field: step, error: error });
  };

  const fileUploadResponse = (responseObj: any) => {
    const step = responseObj["step"];
    const response = responseObj["response"];

    const fieldMappings: { [key: string]: string[] } = {
      fileId: ["fileId", "fileName", "filePath"],
    };

    const fieldNames = fieldMappings[step];

    if (fieldNames) {
      fieldNames.forEach((fieldName: string, key: number) => {
        dispatch({
          type: UPDATE_FIELDS,
          tab: GENERAL_DETAILS,
          field: fieldName,
          value: response[key],
        });
      });
    }
  };

  const deleteResponse = (field: string) => {
    const fieldMappings: { [key: string]: string[] } = {
      fileId: ["fileId", "fileName", "filePath"],
    };

    const fieldNames = fieldMappings[field];

    if (fieldNames) {
      fieldNames.forEach((fieldName: string) => {
        dispatch({
          type: UPDATE_FIELDS,
          tab: GENERAL_DETAILS,
          field: fieldName,
          value: fieldName.endsWith("Id") ? null : "",
        });
      });
    }
  };

  ////////////// ******** Google maps *******************//
  const [showPopup, setShowPopup] = useState(false);
  const [addressType, setAddressType] = useState(0);

  const handleConfirm = (details: AddressDetails | null) => {
    if (details) {
      SetAddressData(details, dispatch, state);
    }

    setShowPopup(false); // Close the popup after confirming the address
  };

  const handleAddPopup = async (type: number) => {
    if (isViewMode) {return;}

    if (type == 1 && state.officeAddressData.zipCode == "") {
      await fetchCompanyDetails(
        "branch",
        state.basicDetails.branch?.value,
        dispatch,
        "create",
        state.basicDetails.office
      ); //Set data related to location details
    }
    setShowPopup(true);
    setAddressType(type);
  };

  const handleClosePopup = () => {
    setShowPopup(false); // or whatever logic you have to close the popup
  };
  //****************** Google maps ************* */

  const initialAddress = (type: number) => {
    // Returns either office address or empty address based on type
    if (type == 1) {
      return {
        street: state.officeAddressData.street,
        nr: state.officeAddressData.number,
        bus: state.officeAddressData.box,
        postal: state.officeAddressData.zipCode,
        // Old: city: state.officeAddressData.city || "",  (a {value, label} option -> "[object Object]")
        // The popup needs the plain name: use cityName, else strip the "8870 - " prefix off the option label.
        city:
          state.officeAddressData.cityName ||
          (typeof state.officeAddressData.city === "string"
            ? state.officeAddressData.city
            : state.officeAddressData.city?.label?.replace(/^\s*\S+\s+-\s+/, "")) ||
          "",
        country: state.officeAddressData.country?.label || "",
      };
    }

    return {
      street: "",
      nr: "",
      bus: "",
      postal: "1000",
      city: "Bruxelles",
      country: "Belgium",
    };
  };

  //To remove the adressData in the general details tab
  const removeAddress = () => {
    if (isViewMode) {return;}
    const addressData = {
      street: "",
      number: "",
      city: null,
      country: null,
      zipCode: "",
      box: "",
      lat: 0,
      lng: 0,
      cityOther: "",
    };

    // Dispatch empty values for each address field
    Object.entries(addressData).forEach(([key, value]) => {
      dispatch({
        type: UPDATE_FIELDS,
        tab: GENERAL_DETAILS,
        field: key,
        value: value,
      });
    });
  };

  return (
    <>
      <div className={`form-border  "form"}`} style={{ paddingTop: "3.4vw" }}>
        <div className="row">
          {/* ******* Address pop up START ********** */}
          <div style={{ marginLeft: "1vw" }} className="col-md-8 ps-0 marginBotttom1">
            <div className="d-flex align-items-center address-icons">
              <LabelField className="tab-subtitle pb-0 marginRight1" title={t("Address")} />

              <div className="d-flex align-items-center marginRight1">
                <Link
                  to={""}
                  title={t("Remove address")}
                  onClick={() => removeAddress()}
                  className="color-dark-pink marginRightPoint5"
                >
                  <DeleteIcon />
                </Link>
                <Link
                  to={""}
                  className="link-color"
                  onClick={() => removeAddress()}
                >
                  {t("Remove address")}
                </Link>
              </div>
              <div className="d-flex align-items-center marginRight1">
                <Link
                  to={""}
                  title={t("Copy address from company file")}
                  onClick={() => handleAddPopup(1)}
                  className="color-dark-pink marginRightPoint5"
                >
                  <CloneIcon />
                </Link>
                <Link
                  to={""}
                  className="link-color"
                  onClick={() => handleAddPopup(1)}
                >
                  {t("Copy address from company file")}
                </Link>
              </div>
              <div className="d-flex align-items-center">
                <Link
                  to={""}
                  title={t("Search address via google map...")}
                  onClick={() => handleAddPopup(0)}
                  className="color-dark-pink marginRightPoint5"
                >
                  <ActiveLocation />
                </Link>
                <Link
                  to={""}
                  className="link-color"
                  onClick={() => handleAddPopup(0)}
                >
                  {t("Search address via google map...")}
                </Link>
              </div>
            </div>
          </div>
          {/* ******* Address pop up END ********** */}
          <div className="col-sm-12 col-md-6">
            <LabelWithInputField
              isMandatory={false}
              name="street"
              handleChange={handleFieldChange}
              value={state.generalDetails.street}
              id="street"
              label={t("Street")}
              type="text"
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <LabelWithInputField
                  isMandatory={false}
                  name="number"
                  handleChange={handleFieldChange}
                  value={state.generalDetails.number}
                  id="number"
                  label={t("Number")}
                  type="text"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <LabelWithInputField
                  isMandatory={false}
                  name="box"
                  handleChange={handleFieldChange}
                  value={state.generalDetails.box}
                  id="box"
                  label={t("Box")}
                  type="text"
                />
              </div>
            </div>
          </div>
          {/* Order: Country -> City -> Zip code. Selecting a city pre-fills an
              empty zip; an existing zip is never overwritten or cleared. */}
          <div className="col-sm-12 col-md-4">
            <SelectWithSearch
              title={t("Country")}
              isMandatory={false}
              search={true}
              options={state.dropDowndata.countryOptions}
              onChange={(e) => handleSelectChange(e, "country")}
              isMulti={false}
              name="country"
              value={state.generalDetails.country}
              isTranslate={true}
            />
          </div>
          {/* <div className="col-sm-12 col-md-4"> */}
          {/* <SelectWithSearch
              title={t("City")}
              isMandatory={false}
              search={true}
              options={state.dropDowndata.cityOptions}
              onChange={(e) => handleSelectChange(e, "city")}
              isMulti={false}
              name="city"
              value={state.generalDetails.city}
              isTranslate={true}
            /> */}
          {/* <LabelWithInputField
              isMandatory={false}
              name="city"
              handleChange={handleFieldChange}
              value={state.generalDetails.city}
              id="city"
              label={t("City")}
              type="text"
            /> */}
          <div className="col-lg-4 col-md-6 autoCompleteFunction">
            <AutoCompleteDropdown
              label={t("City")}
              placeholder={t("Type to search") + "..."}
              microService={CENTRAL_DATA_MANAGEMENT_MICROSERVICE}
              onChange={(e) => handleSelectChange(e, "city")}
              value={state.generalDetails.city}
              columnName="postal_code"
              concat="name"
              modelKey={state.generalDetails?.country?.iso ?? "city"}
              name="city"
              separator=" - "
              isMandatory={true}
              // error={state.errors.city}
              customFetch={true}
              isDisabledStatus={1}
            />
          </div>
          <div className="col-sm-12 col-md-4">
            <LabelWithInputField
              isMandatory={true}
              name="zipCode"
              handleChange={handleFieldChange}
              value={state.generalDetails.zipCode}
              id="zipCode"
              label={t("Zip code")}
              type="text"
              error={state.errors?.zipCode}
            />
          </div>
          {/* </div> */}
          {/* {state.generalDetails.country?.value &&
          state.generalDetails.country.value === 29 ? (
            <div className="col-sm-12 col-md-4">
              <SelectWithSearch
                title={t("City")}
                isMandatory={false}
                search={true}
                options={dummyOptions}
                onChange={(e) => handleSelectChange(e, "city")}
                isMulti={false}
                name="city"
                value={state.generalDetails.city}
                isTranslate={true}
              />
            </div>
          ) : (
            <div className="col-sm-12 col-md-4">
              <LabelWithInputField
                isMandatory={false}
                name="cityOther"
                handleChange={handleFieldChange}
                value={state.generalDetails.cityOther}
                id="cityOther"
                label={t("City")}
                type="text"
              />
            </div>
          )} */}
          <div className="col-md-3">
            <FileUpload
              id="fileId"
              name="fileId"
              label={t("Work post sheet")}
              isMandatory={false}
              edit={false}
              fileId={
                state.generalDetails.fileId ? state.generalDetails.fileId : null
              }
              filePath={
                state.generalDetails.filePath
                  ? state.generalDetails.filePath
                  : ""
              }
              fileName={
                state.generalDetails.fileName
                  ? state.generalDetails.fileName
                  : ""
              }
              style={fileUploadStyleObject}
              multiple={false}
              uploadPath="planning-workPostSheet"
              formats=".pdf,.docx,.doc,.jpg,.jpeg"
              returnFileUploadErrors={(errorResponse: any) =>
                returnFileUploadErrors(errorResponse)
              }
              fileUploadResponse={(uploadResposne: any) =>
                fileUploadResponse(uploadResposne)
              }
              deleteResponse={(field: any) => deleteResponse(field)}
              maxFileSize={10 * 1024 * 1024}
              microserviceURL={`${process.env.REACT_APP_COMPANY_SERVICE}`}
              isViewMode={isViewMode}
            />
            <span className="text-danger">{state.errors?.fileId}</span>
          </div>
          {state.basicDetails?.partiarCommittee?.value === 39 && (
            <>
              {/* <div className="col-md-3">
                <LabelWithInputField
                  isMandatory={false}
                  name="ccCardCurrentMonth"
                  handleChange={handleFieldChange}
                  value={state.generalDetails.ccCardCurrentMonth}
                  id="ccCardCurrentMonth"
                  label={t("Construction control card current month")}
                  type="text"
                />
              </div>
              <div className="col-md-3">
                <LabelWithInputField
                  isMandatory={false}
                  name="ccCardNextMonth"
                  handleChange={handleFieldChange}
                  value={state.generalDetails.ccCardNextMonth}
                  id="ccCardNextMonth"
                  label={t("Construction control card next month")}
                  type="text"
                />
              </div> */}
              <div className="col-sm-12 col-md-3">
                <SelectWithSearch
                  title={t("RSZ Category")}
                  isMandatory={false}
                  search={true}
                  options={state.dropDowndata.rszCategoryOptions}
                  onChange={(e) => handleSelectChange(e, "rszCategory")}
                  isMulti={false}
                  name="rszCategory"
                  value={state.generalDetails.rszCategory}
                  isTranslate={true}
                />
              </div>
            </>
          )}
          <div className="col-sm-12 col-md-3">
            <SelectWithSearch
              title={t("Work regime exemption BV")}
              isMandatory={false}
              search={true}
              options={state.dropDowndata.exemptionOptions}
              onChange={(e) => handleSelectChange(e, "workRegimeBV")}
              isMulti={false}
              name="workRegimeBV"
              value={state.generalDetails.workRegimeBV}
              isTranslate={true}
            />
          </div>
          <div className="col-sm-12 col-md-3 progressiveReturnToWork">
            <LabelField>
              {t("Progressive return to work")}
              <span
                className="ms-2 cursor-pointer contractBillingInfo"
                data-bs-toggle="tooltip"
                title={t("Progressive part time ZIMA002")}
              >
                <InfoIcon />
              </span>
            </LabelField>
            <SelectWithSearch
              title=""
              isMandatory={false}
              search={true}
              options={state.dropDowndata.progressiveOptions}
              onChange={(e) => handleSelectChange(e, "progresWork")}
              isMulti={false}
              name="progresWork"
              value={state.generalDetails.progresWork}
              isTranslate={true}
            />
          </div>
          <div className="col-md-3">
            <LabelWithInputField
              isMandatory={false}
              name="empChar"
              handleChange={handleFieldChange}
              value={state.generalDetails.empChar}
              id="empChar"
              label={t("Employment characteristic")}
              type="text"
            />
          </div>
          <div className="col-sm-12 col-md-3">
            <SelectWithSearch
              title={t("Assign turnover to consultant(s)")}
              isMandatory={false}
              search={true}
              options={state.dropDowndata.consultantOptions}
              onChange={(e) => handleSelectChange(e, "revenueToConsultant")}
              isMulti={false}
              name="revenueToConsultant"
              value={state.generalDetails.revenueToConsultant}
              isTranslate={true}
            />
          </div>
          <div className="col-md-3 checkbox-section hotlistWrapper">
            <div className="ssn-left-sec">
              <CheckBoxField
                label={t("Printed UZM")}
                name="printUZM"
                onChangeHandler={handleFieldChange}
                isChecked={state.generalDetails.printUZM === 1}
                className="me-5"
                disable={true}
              />
              <CheckBoxField
                label={t("Printed customer")}
                name="printCustomer"
                onChangeHandler={handleFieldChange}
                isChecked={state.generalDetails.printCustomer === 1}
                className="me-5"
                disable={true}
              />
            </div>
          </div>
          <div className="col-md-3">
            <LabelWithInputField
              isMandatory={false}
              name="eSignStatus"
              handleChange={handleFieldChange}
              value={state.generalDetails.eSignStatus}
              id="eSignStatus"
              label={t("Status E-signing")}
              type="text"
              isDisabled={true}
            />
          </div>
          <div className="col-md-3">
            <LabelWithInputField
              isMandatory={false}
              name="eSignCancelStatus"
              handleChange={handleFieldChange}
              value={state.generalDetails.eSignCancelStatus}
              id="eSignCancelStatus"
              label={t("E-signing cancellation status")}
              type="text"
              isDisabled={true}
            />
          </div>
        </div>
      </div>
      <AddressSearchPopup
        initialLat={addressType == 1 ? state.officeAddressData.lat ?? state.generalDetails.lat : state.generalDetails.lat}
        initialLng={addressType == 1 ? state.officeAddressData.lng ?? state.generalDetails.lng : state.generalDetails.lng}
        initialAddress={initialAddress(addressType)}
        onConfirm={handleConfirm}
        showAddPopup={showPopup}
        onHide={handleClosePopup}
      />
      <Navigation validStatus={validStatus} />
    </>
  );
};

export default GeneralTab;
